import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import './EditableTable.scss';
import {
    DataGrid,
    GridColDef,
    GridRowModesModel,
    GridRowsProp,
    GridRowModel,
    GridValidRowModel,
    DataGridProps,
    GridCellParams,
    GridRowHeightParams,
    GridRowId,
} from '@mui/x-data-grid';
import EditToolbar from './EditToolbar';
import RowActionColumn from './RowActionColumn';
import { SxProps } from '@mui/material';
import RowVisibilitySwitcherColumn from './CustomColumns/RowVisibilitySwitcherColumn';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { EditorTypeEnum } from '../../models/DataRequestHub/ProjectEditorEnum';
import { wrapInPromise } from '../../utils/WrapInPromise';

interface EditableTableProps {
    columns: GridColDef[];
    rows: GridRowsProp;
    setRows: (newRows: (oldRows: any[]) => any[]) => void;
    isLoading?: boolean;
    fieldToFocus?: string;
    gridApiRef: React.MutableRefObject<GridApiCommunity>;
    editorType: EditorTypeEnum;
    validateAllRows?(): void;
    onColumnResize?(params: { field: string; width: number }): void;
    disableActionColumn?: boolean;
    disableVisibilityColumn?: boolean;
    customActionColumn?: GridColDef;
    onCellClick?(params: GridCellParams): void;
    getDefaultRow?(): GridValidRowModel;
}

const EditableTable = forwardRef((props: EditableTableProps, ref) => {
    const defaultRowPixelHeight = 34;
    const toolbarRef = useRef(null);
    const abortControllerRef = useRef(null);

    useImperativeHandle(ref, () => ({
        addRow() {
            const newRowId = toolbarRef?.current.addRow();
            props.gridApiRef.current.forceUpdate();
            props.gridApiRef.current.setCellFocus(
                newRowId,
                props.fieldToFocus ?? ''
            );
        },
    }));

    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

    const getRowId = (row: GridValidRowModel) => {
        if (props.editorType === EditorTypeEnum.Questionnaire) {
            return row.customQuestionId;
        }
        if (props.editorType === EditorTypeEnum.DataTable) {
            return row.customColumnId;
        }

        if (props.editorType === EditorTypeEnum.DataTableView) {
            return row.customColumnId;
        }

        if (props.editorType === EditorTypeEnum.DataTableData) {
            return row.customRowId;
        }

        if (props.editorType === EditorTypeEnum.LoiFormItem) {
            return row.id;
        }

        return row.gridId ?? row.id;
    };
    const actionColumn = RowActionColumn({
        setRows: props.setRows,
        gridApiRef: props.gridApiRef,
        getRowId: getRowId,
    });

    const visibleColumn = RowVisibilitySwitcherColumn({
        setRows: props.setRows,
        gridApiRef: props.gridApiRef,
        getRowId: getRowId,
        isDataTableView: props.editorType === EditorTypeEnum.DataTableView,
    });

    const columns = (): GridColDef[] => {
        let result = props.disableVisibilityColumn ? [] : [visibleColumn];

        result = result.concat(props.columns);

        if (!props.disableActionColumn) {
            if (props.customActionColumn) {
                result = result.concat([props.customActionColumn]);
            } else {
                result = result.concat([actionColumn]);
            }
        }
        return result;
    };

    const processRowUpdate: DataGridProps['processRowUpdate'] = (
        newRow: GridRowModel
    ) => {
        const updatedRow = { ...newRow, isNew: false };
        props.setRows((prevRows: GridRowsProp) => {
            const rows = prevRows ?? props.rows;
            return rows.map((row) => {
                const rowId = getRowId(row);
                const newRowId = getRowId(newRow);
                if (rowId === newRowId) {
                    return updatedRow;
                }
                return row;
            });
        });
        props.gridApiRef.current.forceUpdate();
        return newRow;
    };

    const sxProps: SxProps = {
        overflow: 'auto',
        maxHeight: '700px',
        border: 'none',
        fontFamily: 'SegoeUI-Semibold',
        fontSize: '13px',
        '& .MuiDataGrid-main': {
            border: '1px solid #e9ecef',
        },
        '& .MuiDataGrid-scrollbar': {
            '--DataGrid-scrollbarSize': '10px !important',
        },
        '& .MuiDataGrid-filler': {
            '--DataGrid-scrollbarSize': '10px !important',
            height: '6px !important',
        },
        '& .MuiDataGrid-columnHeaders': {
            borderRadius: '1px',
            backgroundColor: '#dce0e4 !important',
            '--DataGrid-containerBackground': '#dce0e4 !important',
            color: '#525a64',
        },
        '& .MuiDataGrid-columnHeader': {
            border: '1px solid #e9ecef',
        },
        '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeader:focus':
            {
                outline: 'none',
            },
        '& .MuiDataGrid-virtualScrollerContent': {
            color: '#888f96',
        },
        '& .MuiInputBase-root': {
            color: '#888f96',
            fontFamily: 'SegoeUI-Semibold',
            fontSize: '13px',
        },
        '& .MuiDataGrid-overlayWrapper, .MuiDataGrid-overlayWrapperInner': {
            height: '50px !important',
        },
        '& .MuiDataGrid-cell .MuiDataGrid-cellContent': {
            WebkitBoxOrient: 'vertical',
            display: '-webkit-box',
            WebkitLineClamp: 3,
        },
    };
    const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
    const defaultRowHeightPx = 52;

    const getRowHeight = (params: GridRowHeightParams) => {
        const rowId = params.id;
        return rowHeights[rowId] ?? defaultRowHeightPx;
    };

    const calculateRowHeight = (rowId: GridRowId) => {
        const rowElement = document.querySelector(`[data-id="${rowId}"]`);
        const cellContentElements = rowElement?.querySelectorAll(
            '.MuiDataGrid-cellContent'
        );
        const cellElements = rowElement?.querySelectorAll('.MuiDataGrid-cell');
        const combinedElements = [
            ...Array.from(cellContentElements || []),
            ...Array.from(cellElements || []),
        ];
        let maxHeight = 0;
        combinedElements?.forEach((element: any) => {
            const childElements = element.childNodes;

            childElements?.forEach((child: HTMLElement) => {
                if (child.offsetHeight) {
                    maxHeight = Math.max(maxHeight, child.offsetHeight);
                }
            });
        });
        const heightOffsetPx = 10;
        const result =
            maxHeight > defaultRowHeightPx
                ? maxHeight + heightOffsetPx
                : defaultRowHeightPx;
        return result;
    };

    const updateHeightOfRow = (rowId: GridRowId) => {
        requestAnimationFrame(() => {
            const value = calculateRowHeight(rowId);

            setRowHeights((prevHeights) => ({
                ...prevHeights,
                [rowId]: value,
            }));
        });
    };

    const debounceTableValidation = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            const save = validateAllRows();

            await wrapInPromise(save, 200, abortControllerRef.current.signal);
        } catch (error) {
            console.error(error);
        }
    };

    const validateAllRows = () => {
        return () => {
            if (props.validateAllRows) {
                props?.validateAllRows();
            }
        };
    };

    return (
        <div className="editable-table">
            <DataGrid
                onStateChange={() => {
                    debounceTableValidation();
                }}
                apiRef={props.gridApiRef}
                sx={sxProps}
                onColumnResize={(params) => {
                    if (props.onColumnResize) {
                        props.onColumnResize({
                            width: params.width,
                            field: params.colDef.field,
                        });
                    }
                }}
                columnHeaderHeight={defaultRowPixelHeight}
                showCellVerticalBorder={true}
                disableColumnFilter
                disableColumnMenu
                getRowId={getRowId}
                rows={props.rows}
                columns={columns()}
                processRowUpdate={processRowUpdate}
                slots={{
                    toolbar: EditToolbar as any,
                }}
                getRowHeight={(params) => {
                    const customHeight = getRowHeight(params);
                    return customHeight;
                }}
                onCellEditStart={(params) => {
                    const rowId = params.id;
                    updateHeightOfRow(rowId);
                }}
                onCellKeyDown={(params) => {
                    const rowId = params.id;
                    updateHeightOfRow(rowId);
                }}
                onCellEditStop={(params) => {
                    const rowId = params.id;
                    setRowHeights((prevHeights) => ({
                        ...prevHeights,
                        [rowId]: defaultRowHeightPx,
                    }));
                }}
                slotProps={{
                    toolbar: {
                        rows: props.rows,
                        setRows: props.setRows,
                        setRowModesModel,
                        isLoading: props.isLoading,
                        focusField: props.fieldToFocus,
                        ref: toolbarRef,
                        editorType: props.editorType,
                        getDefaultRow: props.getDefaultRow,
                    },
                    baseSelect: {
                        MenuProps: {
                            PaperProps: {
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        minHeight: '36px',
                                    },
                                },
                            },
                        },
                    },
                }}
                hideFooterSelectedRowCount
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            allowedFileFormat: false,
                            maxTextSize: false,
                        },
                    },
                }}
                onCellClick={(params) => {
                    if (props.onCellClick) {
                        props?.onCellClick(params);
                    }
                }}
            />
        </div>
    );
});

export default EditableTable;
